import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect, type SearchableOption } from "@/components/searchable-select";
import { useSalesAgents, useGetInsuredByDot, useUpdateInsured } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneDisplay, unformatPhone } from "@/lib/formatters";
import type { Insured, Policy } from "@shared/schema";
import { DeleteInsuredDialog } from "./DeleteInsuredDialog";
import { RETENTION_AGENT_TEAM } from "@shared/retentionAgents";
import { mergeSalesAgentNames } from "@shared/salesAgents";

// CSR-762 — Sentinel for the dropdown's "None" option. Stored in form state
// so the Radix Select always has a non-empty `value` (Radix forbids ""), and
// mapped back to an empty string on submit so the existing form contract
// (empty → null) keeps working.
const RETENTION_AGENT_NONE = "__none__";

const ENTITY_TYPES = [
  "Sole Prop",
  "LLC",
  "Corporation",
  "Partnership",
  "Other",
] as const;

interface Props {
  insured: Insured;
  children: ReactNode;
  // CSR-1970 — the insured's policies, and whether that list actually LOADED.
  // Both are forwarded to the delete confirmation, which lists every policy the
  // delete will take with it and refuses to proceed when it cannot say what
  // those are. Optional so callers that never surface Delete are unaffected.
  policies?: Policy[];
  policiesLoaded?: boolean;
  // CSR-1881 — producer names from this account's policies (who sold it). Merged
  // into the Sales Agent options so a former/terminated producer stays selectable
  // on their historical account, without polluting every account's dropdown.
  policyProducers?: string[];
}

/**
 * CSR-1881 — Sales Agent dropdown options for one account: the current Flint
 * roster, plus the producer(s) who sold THIS account's policies, plus the agent
 * already saved on the insured. Keeps former/terminated producers selectable on
 * their historical accounts while staying selection-only, deduped, alphabetical.
 */
export function salesAgentOptionsFor(
  roster: string[],
  policyProducers: string[],
  savedSalesAgent: string | null | undefined,
): SearchableOption[] {
  const saved = savedSalesAgent ? [savedSalesAgent] : [];
  return mergeSalesAgentNames(roster, policyProducers, saved).map((name) => ({
    value: name,
    label: name,
  }));
}

interface FormState {
  name: string;
  dbaName: string;
  businessOrg: string;
  dotNumber: string;
  radius: string;
  companyOfficer: string;
  email: string;
  telephone: string;
  salesAgent: string;
  retentionAgent: string;
}

function buildForm(insured: Insured): FormState {
  return {
    name: insured.name ?? "",
    dbaName: insured.dbaName ?? "",
    businessOrg: insured.businessOrg ?? "",
    dotNumber: insured.dotNumber ?? "",
    radius: insured.radius ?? "",
    companyOfficer: insured.companyOfficer ?? "",
    email: insured.email ?? "",
    telephone: insured.telephone ?? "",
    salesAgent: insured.salesAgent ?? "",
    retentionAgent: insured.retentionAgent ?? "",
  };
}

const inputClass = "rounded-lg h-10";

export function EditInsuredDialog({
  insured,
  children,
  policyProducers = [],
  policies,
  policiesLoaded,
}: Props) {
  const [open, setOpen] = useState(false);
  // CSR-1970 — Delete lives in here rather than beside Edit in the sidebar,
  // matching EditPolicyDialog: a destructive action sits behind the edit surface
  // you already had to open, not one click from the profile.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState<FormState>(() => buildForm(insured));
  const [dotDupMatch, setDotDupMatch] = useState<{ id: string; name: string | null } | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const update = useUpdateInsured();
  const { toast } = useToast();
  const byDot = useGetInsuredByDot(form.dotNumber.trim() || null);
  const { data: salesAgents } = useSalesAgents();
  const salesAgentOptions = useMemo(
    () => salesAgentOptionsFor(salesAgents ?? [], policyProducers, insured.salesAgent),
    [salesAgents, policyProducers, insured.salesAgent],
  );

  useEffect(() => {
    if (open) {
      setForm(buildForm(insured));
      setDotDupMatch(null);
      setShowErrors(false);
    }
  }, [open, insured]);

  const set = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit =
    form.name.trim() !== "" &&
    form.companyOfficer.trim() !== "" &&
    form.email.trim() !== "" &&
    form.telephone.trim() !== "";

  // CSR-1752 — Instead of a silently disabled Save button, let the user click
  // Save and surface an inline error on each empty required field. `showErrors`
  // flips on the first blocked submit; each field's error clears the moment it
  // is filled.
  const requiredMissing = (value: string) =>
    showErrors && value.trim() === "";

  // CSR-1140 — Flag an incomplete/malformed phone (red border + message) but do
  // NOT add it to `canSubmit`: legacy insureds may carry a malformed phone, and
  // a user editing an unrelated field must still be able to save.
  const phoneInvalid =
    form.telephone.trim() !== "" &&
    form.telephone.replace(/\D/g, "").length !== 10;

  const performUpdate = () => {
    update.mutate(
      {
        id: insured.id,
        data: {
          name: form.name.trim() || null,
          dbaName: form.dbaName.trim() || null,
          businessOrg: form.businessOrg.trim() || null,
          dotNumber: form.dotNumber.trim() || null,
          radius: form.radius.trim() || null,
          companyOfficer: form.companyOfficer.trim() || null,
          email: form.email.trim() || null,
          telephone: form.telephone.trim() || null,
          salesAgent: form.salesAgent.trim() || null,
          retentionAgent: form.retentionAgent.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
          toast({ title: "Insured updated" });
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to update insured';
          const isDcFailure = message.includes('DC write failed');
          toast({
            title: isDcFailure ? 'Data Center rejected the edit' : 'Failed to update insured',
            description: message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const dotChanged =
    form.dotNumber.trim() !== (insured.dotNumber ?? "").trim();

  // CSR-1752 — Single required-field gate shared by every mutation path (Save
  // and the DOT "Update anyway" confirmation), so a field cleared after the
  // duplicate warning appears can never slip an incomplete update through.
  const ensureRequired = () => {
    if (canSubmit) return true;
    setShowErrors(true);
    return false;
  };

  const submit = async () => {
    if (update.isPending) return;
    if (!ensureRequired()) return;

    // Only check for a DOT collision when the DOT actually changed — editing an
    // unrelated field on an insured whose DOT is unchanged (or already shares a
    // DOT with a legacy duplicate) must never trigger the warning.
    if (dotChanged && form.dotNumber.trim()) {
      const res = await byDot.refetch();
      const match = res.data as { id?: string; name?: string | null } | null;
      if (match?.id && match.id !== insured.id) {
        setDotDupMatch({ id: match.id, name: match.name ?? null });
        return;
      }
    }
    performUpdate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        // CSR-1500: lay the modal out as a flex column — pinned header, a single
        // scrollable field region, and a pinned footer — so the Save button is
        // always visible regardless of window height. Overrides the shared
        // DialogContent's grid/padding/overflow; the base max-h-[calc(100vh-2rem)]
        // still bounds the column so the body region can scroll.
        className="sm:max-w-lg flex flex-col gap-0 p-0 overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <DialogHeader className="shrink-0 px-6 pt-6">
          <DialogTitle>Edit Insured</DialogTitle>
        </DialogHeader>

        <div
          data-testid="edit-insured-scroll-body"
          className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4"
        >
          <div
            className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
            data-testid="edit-insured-sync-banner"
          >
            Some fields here (FMCSA address, fleet counts, agent assignments) are
            synced from the Data Center. Manual edits apply immediately but may be
            overwritten on the next sync from FMCSA/NowCerts.
          </div>

          {dotDupMatch && (
            <div
              data-testid="edit-insured-dot-dup-warning"
              className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
            >
              <p>
                Another insured already has this DOT number:{" "}
                <strong>{dotDupMatch.name ?? "(no name)"}</strong>. Do you want to
                update this insured anyway?
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="cursor-pointer"
                  data-testid="edit-insured-dot-dup-update-anyway"
                  disabled={update.isPending}
                  onClick={() => {
                    setDotDupMatch(null);
                    if (!ensureRequired()) return;
                    performUpdate();
                  }}
                >
                  Update anyway
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="cursor-pointer"
                  data-testid="edit-insured-dot-dup-cancel"
                  disabled={update.isPending}
                  onClick={() => setDotDupMatch(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="edit-name">
                Company / Insured Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-name"
                autoFocus
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className={`${inputClass} ${requiredMissing(form.name) ? "border-destructive" : ""}`}
                placeholder="e.g. Acme Trucking LLC"
                data-testid="edit-insured-name"
              />
              {requiredMissing(form.name) && (
                <p
                  data-testid="edit-insured-name-error"
                  className="text-xs text-destructive"
                >
                  Company / Insured Name is required.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-dba">DBA Name</Label>
              <Input
                id="edit-dba"
                value={form.dbaName}
                onChange={(e) => set("dbaName", e.target.value)}
                className={inputClass}
                placeholder="e.g. Acme Express"
                data-testid="edit-insured-dba"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-entity">Entity Type</Label>
                <Select
                  value={form.businessOrg || undefined}
                  onValueChange={(v) => set("businessOrg", v)}
                >
                  <SelectTrigger
                    id="edit-entity"
                    className={inputClass}
                    data-testid="edit-insured-entity"
                  >
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-dot">DOT Number</Label>
                <Input
                  id="edit-dot"
                  value={form.dotNumber}
                  onChange={(e) => {
                    set("dotNumber", e.target.value);
                    setDotDupMatch(null);
                  }}
                  className={inputClass}
                  placeholder="e.g. 1234567"
                  data-testid="edit-insured-dot"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-radius">Radius (miles)</Label>
              <Input
                id="edit-radius"
                value={form.radius}
                onChange={(e) => set("radius", e.target.value)}
                className={inputClass}
                placeholder="e.g. 500 miles or Unlimited"
                data-testid="edit-insured-radius"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold">
                Contact Information <span className="text-destructive">*</span>
              </h3>
              <div className="space-y-2">
                <Label htmlFor="edit-contact">
                  Contact Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-contact"
                  value={form.companyOfficer}
                  onChange={(e) => set("companyOfficer", e.target.value)}
                  className={`${inputClass} ${requiredMissing(form.companyOfficer) ? "border-destructive" : ""}`}
                  placeholder="e.g. John Smith"
                  data-testid="edit-insured-contact"
                />
                {requiredMissing(form.companyOfficer) && (
                  <p
                    data-testid="edit-insured-contact-error"
                    className="text-xs text-destructive"
                  >
                    Contact Name is required.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={`${inputClass} ${requiredMissing(form.email) ? "border-destructive" : ""}`}
                    placeholder="name@company.com"
                    data-testid="edit-insured-email"
                  />
                  {requiredMissing(form.email) && (
                    <p
                      data-testid="edit-insured-email-error"
                      className="text-xs text-destructive"
                    >
                      Email is required.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">
                    Phone <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="edit-phone"
                    value={formatPhoneDisplay(form.telephone)}
                    onChange={(e) => set("telephone", unformatPhone(e.target.value))}
                    className={`${inputClass} ${phoneInvalid || requiredMissing(form.telephone) ? "border-destructive" : ""}`}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                    data-testid="edit-insured-phone"
                  />
                  {(phoneInvalid || requiredMissing(form.telephone)) && (
                    <p
                      data-testid="edit-insured-phone-error"
                      className="text-xs text-destructive"
                    >
                      {requiredMissing(form.telephone)
                        ? "Phone is required."
                        : "Enter a valid 10-digit phone number."}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Assignment</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-sales-agent">Sales Agent</Label>
                  <SearchableSelect
                    value={form.salesAgent}
                    onChange={(v) => set("salesAgent", v)}
                    options={salesAgentOptions}
                    placeholder="Select a sales agent…"
                  />
                  {/* Test-only handle: drives state without going through Radix
                      popover (hard to drive in happy-dom). */}
                  <input
                    type="text"
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                    data-testid="edit-insured-sales-agent"
                    value={form.salesAgent}
                    onChange={(e) => set("salesAgent", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-retention-agent">Retention Agent</Label>
                  <Select
                    value={form.retentionAgent || RETENTION_AGENT_NONE}
                    onValueChange={(v) =>
                      set(
                        "retentionAgent",
                        v === RETENTION_AGENT_NONE ? "" : v,
                      )
                    }
                  >
                    <SelectTrigger
                      id="edit-retention-agent"
                      className={inputClass}
                      data-testid="edit-insured-retention-agent"
                    >
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={RETENTION_AGENT_NONE}>None</SelectItem>
                      {RETENTION_AGENT_TEAM.map((member) => (
                        <SelectItem key={member} value={member}>
                          {member}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CSR-1970 — all three actions grouped at the right, Delete leftmost of
            the group. Matches how EditPolicyDialog actually renders: its
            DialogFooter carries `sm:justify-end`, which survives alongside the
            `justify-between` it passes (different breakpoint variants), so at
            desktop width its buttons bunch right rather than splitting apart.

            The col-reverse/row pair is DialogFooter's own responsive shape, and
            it is load-bearing rather than copied for symmetry: three
            whitespace-preserving buttons in a fixed single row overflow a narrow
            viewport, and this dialog is `overflow-hidden`, so they would be
            CLIPPED rather than wrapped — Save unreachable on a phone. Stacking
            below `sm` keeps every action reachable, and col-REVERSE puts the
            primary on top and the destructive one last, so the button nearest
            the thumb is not Delete. */}
        <div
          data-testid="edit-insured-footer"
          className="shrink-0 flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-end"
        >
          <Button
            variant="destructive"
            className="rounded-full gap-2 cursor-pointer"
            onClick={() => setDeleteOpen(true)}
            disabled={update.isPending}
            data-testid="edit-insured-delete"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
          <Button
            variant="outline"
            className="rounded-full cursor-pointer"
            onClick={() => setOpen(false)}
            disabled={update.isPending}
            data-testid="edit-insured-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full gap-2 cursor-pointer"
            onClick={submit}
            disabled={update.isPending}
            data-testid="edit-insured-submit"
          >
            {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
      <DeleteInsuredDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        insured={insured}
        policies={policies ?? []}
        // A caller that forwards no policies has not told us the list is empty —
        // it has told us nothing. Treat that as unknown so the confirmation
        // fails closed, exactly as a failed fetch does.
        policiesLoaded={policies !== undefined && policiesLoaded !== false}
        // Close the edit surface too; the profile behind it is about to 404.
        onDeleted={() => setOpen(false)}
      />
    </Dialog>
  );
}
